import React, { useEffect, useState, useMemo } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
} from '@chakra-ui/react';
import {
  DotsThree,
  Plus,
  PencilSimple,
  Trash,
  SortAscending,
  ArrowUp,
  CalendarBlank,
  Clock,
  ClockCounterClockwise,
  TextAa,
} from 'phosphor-react';
import { IKanbanColumn, IKanbanCard } from '../../hooks/useKanban';
import { KanbanCard } from './KanbanCard';
import { ehNovo, porChegada, textoDaChegada } from './chegadaNaColuna';

type SortOption = 'rank' | 'prioridade' | 'titulo' | 'data_criacao' | 'chegada' | 'expiracao';

const PRIORIDADE_ORDEM: Record<string, number> = { alta: 0, media: 1, baixa: 2 };

const sortCards = (cards: IKanbanCard[], sortBy: SortOption): IKanbanCard[] => {
  const sorted = [...cards];
  switch (sortBy) {
    case 'prioridade':
      return sorted.sort(
        (a, b) => (PRIORIDADE_ORDEM[a.prioridade] ?? 1) - (PRIORIDADE_ORDEM[b.prioridade] ?? 1),
      );
    case 'titulo':
      return sorted.sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
    case 'data_criacao':
      return sorted.sort(
        (a, b) => new Date(b.dt_criacao).getTime() - new Date(a.dt_criacao).getTime(),
      );
    case 'chegada':
      return sorted.sort(porChegada);
    case 'expiracao':
      return sorted.sort((a, b) => {
        if (!a.expira_em && !b.expira_em) return 0;
        if (!a.expira_em) return 1;
        if (!b.expira_em) return -1;
        return new Date(a.expira_em).getTime() - new Date(b.expira_em).getTime();
      });
    default:
      return sorted;
  }
};

const SORT_LABELS: Record<SortOption, string> = {
  rank: 'Manual',
  prioridade: 'Prioridade',
  titulo: 'Titulo',
  data_criacao: 'Mais recentes',
  chegada: 'Chegada na coluna',
  expiracao: 'Expiracao',
};

const SORT_ICONS: Record<SortOption, React.ReactNode> = {
  rank: <SortAscending size={13} />,
  prioridade: <ArrowUp size={13} />,
  titulo: <TextAa size={13} />,
  data_criacao: <Clock size={13} />,
  chegada: <ClockCounterClockwise size={13} />,
  expiracao: <CalendarBlank size={13} />,
};

// No quadro do evento, a ordem escolhida fica neste navegador, por coluna (antes voltava a "Manual"
// a cada recarga). Os outros quadros seguem como eram.
const chaveDaOrdem = (columnId: number) => `kanban:ordem-da-coluna:${columnId}`;

export const ordemGuardada = (columnId: number): SortOption => {
  try {
    const salva = window.localStorage.getItem(chaveDaOrdem(columnId));
    return salva && salva in SORT_LABELS ? (salva as SortOption) : 'rank';
  } catch {
    return 'rank';
  }
};

const guardarOrdem = (columnId: number, ordem: SortOption) => {
  try {
    if (ordem === 'rank') window.localStorage.removeItem(chaveDaOrdem(columnId));
    else window.localStorage.setItem(chaveDaOrdem(columnId), ordem);
  } catch {
    // sem localStorage (aba anônima bloqueada): a ordem só não é lembrada
  }
};

interface KanbanColumnProps {
  column: IKanbanColumn;
  index: number;
  canManage: boolean;
  canEditProtected?: boolean;
  canCreateCard?: boolean;
  // Espelho read-only (quadro de módulo, task #757): sem drag e sem "Novo card".
  isReadOnly?: boolean;
  /** Faixa opcional logo abaixo do cabeçalho (ex.: template de contrato do evento). */
  topo?: React.ReactNode;
  /** Selo "Novo" e "chegou há…" nos cards (quadro do evento). */
  destacarRecentes?: boolean;
  /** Relógio do board (ms), para o "Novo" expirar com a tela aberta. */
  agora?: number;
  // eslint-disable-next-line no-unused-vars
  onCardClick: (card: IKanbanCard) => void;
  // eslint-disable-next-line no-unused-vars
  onAddCardClick: (column: IKanbanColumn) => void;
  // eslint-disable-next-line no-unused-vars
  onEditColumn: (column: IKanbanColumn) => void;
  // eslint-disable-next-line no-unused-vars
  onArchiveColumn: (columnId: number) => void;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  index,
  canManage,
  canEditProtected = false,
  canCreateCard = true,
  isReadOnly = false,
  topo,
  destacarRecentes = false,
  agora,
  onCardClick,
  onAddCardClick,
  onEditColumn,
  onArchiveColumn,
}) => {
  const [sortBy, setSortByState] = useState<SortOption>(() =>
    destacarRecentes ? ordemGuardada(column.id) : 'rank',
  );
  const setSortBy = (ordem: SortOption) => {
    setSortByState(ordem);
    if (destacarRecentes) guardarOrdem(column.id, ordem);
  };
  // "É quadro de evento" vem da lista de projetos, que pode chegar depois do board:
  // a coluna já montou com 'rank' e precisa buscar a ordem guardada quando descobre.
  useEffect(() => {
    if (destacarRecentes) setSortByState(ordemGuardada(column.id));
  }, [destacarRecentes, column.id]);

  const displayCards = useMemo(() => {
    const cards = column.cards || [];
    if (sortBy === 'rank') return cards;
    return sortCards(cards, sortBy);
  }, [column.cards, sortBy]);

  const columnContent = (
    <Box
      bg="gray.50"
      borderRadius="16px"
      minW="320px"
      maxW="320px"
      h="fit-content"
      maxH="calc(100vh - 200px)"
      display="flex"
      flexDirection="column"
      boxShadow="0 1px 3px rgba(0,0,0,0.06)"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
    >
      {/* Header */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100">
        <Flex px={4} pt={3} pb={3} alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={2}>
            <Text fontWeight="600" fontSize="14px" color="gray.700" letterSpacing="-0.01em">
              {column.titulo}
            </Text>
            <Flex
              bg="gray.100"
              color="gray.500"
              fontSize="11px"
              fontWeight="700"
              px={2}
              py={0.5}
              borderRadius="full"
              minW="22px"
              justifyContent="center"
            >
              {column.cards?.length || 0}
            </Flex>
          </Flex>

          <Flex gap={0.5}>
            {/* Botao de ordenacao */}
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<SortAscending size={15} weight={sortBy !== 'rank' ? 'fill' : 'bold'} />}
                variant="ghost"
                size="xs"
                borderRadius="lg"
                aria-label="Ordenar"
                color={sortBy !== 'rank' ? 'blue.500' : 'gray.400'}
                _hover={{ bg: 'gray.100', color: sortBy !== 'rank' ? 'blue.600' : 'gray.600' }}
              />
              <Portal>
                <MenuList
                  borderRadius="xl"
                  boxShadow="lg"
                  border="1px solid"
                  borderColor="gray.100"
                  py={1}
                  minW="170px"
                  fontSize="13px"
                >
                  {(Object.keys(SORT_LABELS) as SortOption[]).map(opt => (
                    <MenuItem
                      key={opt}
                      icon={SORT_ICONS[opt] as React.ReactElement}
                      onClick={() => setSortBy(opt)}
                      borderRadius="lg"
                      mx={1}
                      bg={sortBy === opt ? 'blue.50' : 'transparent'}
                      color={sortBy === opt ? 'blue.600' : 'gray.600'}
                      fontWeight={sortBy === opt ? '600' : '400'}
                      _hover={{ bg: sortBy === opt ? 'blue.50' : 'gray.50' }}
                    >
                      {SORT_LABELS[opt]}
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>

            {/* Menu de opcoes (oculto para colunas protegidas sem permissão e no espelho read-only) */}
            {!isReadOnly && canManage && (!column.protegida || canEditProtected) && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<DotsThree size={18} weight="bold" />}
                  variant="ghost"
                  size="xs"
                  borderRadius="lg"
                  aria-label="Opcoes da coluna"
                  color="gray.400"
                  _hover={{ bg: 'gray.100', color: 'gray.600' }}
                />
                <Portal>
                  <MenuList
                    borderRadius="xl"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.100"
                    py={1}
                    minW="160px"
                  >
                    <MenuItem
                      icon={<PencilSimple size={15} />}
                      onClick={() => onEditColumn(column)}
                      borderRadius="lg"
                      mx={1}
                      fontSize="13px"
                      _hover={{ bg: 'gray.50' }}
                    >
                      Editar
                    </MenuItem>
                    <MenuItem
                      icon={<Trash size={15} />}
                      color="red.500"
                      onClick={() => onArchiveColumn(column.id)}
                      borderRadius="lg"
                      mx={1}
                      fontSize="13px"
                      _hover={{ bg: 'red.50' }}
                    >
                      Arquivar
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            )}
          </Flex>
        </Flex>
        {topo}
      </Box>

      {/* Cards area */}
      <Droppable droppableId={`column-${column.id}`} type="card">
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            px={2}
            py={2}
            flex={1}
            overflowY="auto"
            minH="60px"
            bg={snapshot.isDraggingOver ? 'blue.50' : 'transparent'}
            transition="background 0.2s ease"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
              '&::-webkit-scrollbar-thumb:hover': { background: '#A0AEC0' },
            }}
          >
            {displayCards.length === 0 && !snapshot.isDraggingOver && (
              <Flex
                h="60px"
                alignItems="center"
                justifyContent="center"
                borderRadius="12px"
                border="2px dashed"
                borderColor="gray.200"
              >
                <Text fontSize="12px" color="gray.400" fontWeight="500">
                  Arraste cards aqui
                </Text>
              </Flex>
            )}
            {displayCards.map((card, cardIndex) => (
              <KanbanCard
                key={card.id}
                card={card}
                index={cardIndex}
                isReadOnly={isReadOnly}
                destacarRecentes={destacarRecentes}
                novo={destacarRecentes && ehNovo(card, agora)}
                chegada={destacarRecentes ? textoDaChegada(card, agora) : null}
                onCardClick={onCardClick}
              />
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>

      {/* Add card button */}
      {!isReadOnly && canManage && canCreateCard && (
        <Box px={2} pb={2}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Plus size={15} weight="bold" />}
            w="100%"
            justifyContent="flex-start"
            color="gray.400"
            fontWeight="500"
            fontSize="13px"
            borderRadius="10px"
            h="34px"
            _hover={{ bg: 'white', color: 'gray.600' }}
            onClick={() => onAddCardClick(column)}
          >
            Novo card
          </Button>
        </Box>
      )}
    </Box>
  );

  if (canManage && !isReadOnly) {
    return (
      <Draggable
        draggableId={`column-${column.id}`}
        index={index}
        isDragDisabled={column.protegida && !canEditProtected}
      >
        {provided => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...(column.protegida && !canEditProtected ? {} : provided.dragHandleProps)}
            flexShrink={0}
          >
            {columnContent}
          </Box>
        )}
      </Draggable>
    );
  }

  return <Box flexShrink={0}>{columnContent}</Box>;
};
